import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, IconButton, Slide, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useQuery } from '@apollo/client/react';
import gql from 'graphql-tag';

const STORAGE_KEY = 'ouaaa_map_alert_banner_dismissed';
const COOLDOWN_DAYS = 7;

const GET_COLLECTIONS_LABELS = gql`
  {
    collections {
      id
      position
      entries {
        id
        label
        subEntries {
          id
          label
          subEntries {
            id
            label
          }
        }
      }
    }
  }
`;

interface MapAlertBannerProps {
  filters: Record<string, any> | null;
  hasActiveSavedFilter: boolean;
  onActivate: () => void;
}

function flattenEntries(entries: any[]): Array<{ id: string; label: string }> {
  if (!entries) return [];
  return entries.flatMap((e) => [
    { id: String(e.id), label: e.label },
    ...flattenEntries(e.subEntries || []),
  ]);
}

function isDismissed(): boolean {
  if (typeof window === 'undefined') return true;
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return false;
  const ts = parseInt(raw, 10);
  if (Number.isNaN(ts)) return false;
  const days = (Date.now() - ts) / (1000 * 60 * 60 * 24);
  return days < COOLDOWN_DAYS;
}

function MapAlertBanner({ filters, hasActiveSavedFilter, onActivate }: MapAlertBannerProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [dismissed, setDismissed] = useState<boolean>(true);
  const { data: collectionsData } = useQuery<any>(GET_COLLECTIONS_LABELS, {
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    setDismissed(isDismissed());
  }, []);

  const hasMeaningfulFilter = useMemo(() => {
    if (!filters) return false;
    if (filters.search) return true;
    if (filters.locationLat != null && filters.locationLng != null) return true;
    if (filters.favoritesForUser) return true;
    if (Array.isArray(filters.actorsCollective) && filters.actorsCollective.length > 0) return true;
    if (Array.isArray(filters.entries)) {
      return filters.entries.some((arr: any) => Array.isArray(arr) && arr.length > 0);
    }
    return false;
  }, [filters]);

  const summary = useMemo<string>(() => {
    if (!filters) return '';
    const parts: string[] = [];

    if (collectionsData?.collections && Array.isArray(filters.entries)) {
      const lookup = new Map<string, string>();
      collectionsData.collections.forEach((col: any) => {
        flattenEntries(col.entries || []).forEach(({ id, label }) => {
          lookup.set(id, label);
        });
      });
      const labels: string[] = [];
      filters.entries.forEach((arr: any) => {
        if (Array.isArray(arr)) {
          arr.forEach((id: any) => {
            const lab = lookup.get(String(id));
            if (lab) labels.push(lab);
          });
        }
      });
      if (labels.length === 1) parts.push(labels[0]);
      else if (labels.length === 2) parts.push(`${labels[0]} et ${labels[1]}`);
      else if (labels.length > 2) parts.push(`${labels[0]}, ${labels[1]} +${labels.length - 2}`);
    }

    if (filters.locationLabel) {
      parts.push(`près de ${filters.locationLabel}`);
    }

    if (filters.search) {
      parts.push(`« ${filters.search} »`);
    }

    return parts.join(' · ');
  }, [filters, collectionsData]);

  const visible = hasMeaningfulFilter && !hasActiveSavedFilter && !dismissed;

  const handleDismiss = () => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    }
    setDismissed(true);
  };

  const handleActivate = () => {
    onActivate();
  };

  return (
    <Slide direction="up" in={visible} mountOnEnter unmountOnExit>
      <Box
        role="region"
        aria-label="Suggestion d'alerte"
        sx={{
          position: 'fixed',
          left: { xs: 8, sm: 16 },
          right: { xs: 8, sm: 16 },
          bottom: { xs: 70, sm: 16 },
          zIndex: 1100,
          maxWidth: 560,
          mx: 'auto',
          bgcolor: 'white',
          border: '1px solid #e0e0e0',
          borderLeft: '4px solid #69b6a9',
          borderRadius: 2,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          p: { xs: 1.5, sm: 2 },
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 1.5,
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
          <NotificationsActiveIcon sx={{ color: '#2c367e', flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 600,
                color: '#2c367e',
                fontSize: { xs: '0.9rem', sm: '0.95rem' },
                lineHeight: 1.2,
              }}
            >
              {summary ? `Tu filtres par ${summary} ?` : 'Tu as appliqué un filtre ?'}
            </Typography>
            <Typography
              sx={{
                color: '#555',
                fontSize: { xs: '0.78rem', sm: '0.82rem' },
                lineHeight: 1.3,
                mt: 0.25,
              }}
            >
              Sauvegarde ce filtre pour être alerté·e des nouveaux acteurs et événements.
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, alignSelf: { xs: 'stretch', sm: 'auto' } }}>
          <Button
            variant="contained"
            size={isMobile ? 'small' : 'medium'}
            onClick={handleActivate}
            sx={{
              backgroundColor: '#2c367e',
              color: 'white',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              whiteSpace: 'nowrap',
              flex: { xs: 1, sm: 'unset' },
              '&:hover': { backgroundColor: '#1f2860' },
            }}
          >
            Activer une alerte
          </Button>
          <IconButton size="small" onClick={handleDismiss} aria-label="Fermer">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Slide>
  );
}

export default MapAlertBanner;
